import { useCallback, useReducer } from "react";
import { OrderDispatchOption, Product } from "~/types";
import { usePathname, useRouter } from "next/navigation";
import { findDeal } from "~/hooks/deal";
import VoucherCodeForm from "./VoucherCodeForm";
import { VoucherCodeInLine } from "./VoucherCodeInLine";
import { useVoucherCode } from "~/hooks/voucher-code";
import { useCart } from "~/hooks/cart";
import { useBaseMenu } from "~/hooks/menu-v2";
import RedeemCodeError from "./RedeemCodeError";
import { VoucherCodeDeal } from "./VoucherCodeDeal";
import { useUser } from "~/hooks/user";
import { VoucherCodeNotLoggedIn } from "./VoucherCodeNotLoggedIn";
import { VoucherCodeDialog } from "./VoucherCodeDialog/VoucherCodeDialog";
import { ClaimSuccessModal } from "../ClaimPromotionModal/ClaimSuccessModal";
import { PromotionRewardType } from "~/graphql-client/generated";

interface Deal {
  product: Product;
  cartItemLineItemId?: string;
}

type VoucherAction =
  | { type: "show" }
  | { type: "hide" }
  | { type: "value"; value: string }
  | { type: "busy" }
  | {
      type: "voucher";
      message: string;
      title: string;
      rewardType?: PromotionRewardType | null;
    }
  | { type: "error"; message: string }
  | { type: "deal"; deal: Deal }
  | { type: "not-logged-in" };

interface VoucherState {
  value: string;
  error?: string;
  voucher?: {
    title: string;
    message: string;
    rewardType: PromotionRewardType | null;
  };
  show: boolean;
  deal?: Deal;
  busy?: boolean;
  notLoggedIn?: boolean;
}

const voucherReducer = (
  state: VoucherState,
  action: VoucherAction,
): VoucherState => {
  switch (action.type) {
    case "show":
      return { ...state, show: true };
    case "hide":
      return {
        ...state,
        show: false,
        busy: false,
        value: "",
        deal: undefined,
        voucher: undefined,
        error: undefined,
        notLoggedIn: undefined,
      };
    case "busy":
      return { ...state, busy: true };
    case "value":
      return { ...state, value: action.value.toUpperCase() };
    case "deal":
      return { ...state, busy: false, deal: action.deal };

    case "voucher":
      return {
        ...state,
        busy: false,
        voucher: {
          title: action.title,
          message: action.message,
          rewardType: action.rewardType ?? null,
        },
      };

    case "error":
      return { ...state, error: action.message, busy: false };

    case "not-logged-in":
      return { ...state, notLoggedIn: true };
    default:
      break;
  }
  return state;
};

export function VoucherCodePanel({
  loading,
  locationRef,
  fulfillmentType,
  design,
  onClickVoucherContinue,
}: {
  locationRef: string;
  loading: boolean;
  fulfillmentType: OrderDispatchOption | null;
  design: "in-line" | "classic";
  onClickVoucherContinue?: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useUser();
  const { redeemCode } = useVoucherCode();
  const { cart } = useCart();
  const { menu } = useBaseMenu(locationRef, fulfillmentType, null);

  const [{ show, value, deal, error, voucher, busy, notLoggedIn }, dispatch] =
    useReducer(voucherReducer, {
      show: false,
      value: "",
    });

  const handleSubmit = useCallback(async () => {
    try {
      if (!value) return;

      dispatch({ type: "busy" });
      const deal = findDeal(menu, cart, value);

      if (deal) {
        dispatch({ type: "deal", deal });
        return;
      }

      if (!user) {
        dispatch({
          type: "not-logged-in",
        });
        return;
      }

      if (value === "DAVEORONI") {
        /**
         * This is an easter egg.
         * The idea is to not tell anyone and see how long it takes to be discovered.
         * -- Stevo
         **/
        dispatch({ type: "deal", deal: daveDeal });
        return;
      }

      const result = await redeemCode(value);

      if (result) {
        dispatch({
          type: "voucher",
          message: result.message,
          title: result.title,
          rewardType: "rewardType" in result ? result.rewardType : null,
        });
      }
    } catch (err) {
      const e = err as Error;
      dispatch({ type: "error", message: e.message });
    }
  }, [cart, menu, redeemCode, user, value]);

  const handleContinue = () => {
    dispatch({ type: "hide" });
    if (onClickVoucherContinue) {
      onClickVoucherContinue();
    }
  };

  const handleLogin = () => {
    router.replace(
      `/login?next=${encodeURIComponent(pathname)}&location=${locationRef}`,
    );
  };

  return (
    <>
      {design === "classic" && (
        <VoucherCodeForm
          voucherCode={value}
          onCodeChange={(value) => dispatch({ type: "value", value })}
          onSubmit={handleSubmit}
        />
      )}

      {design === "in-line" && (
        <VoucherCodeInLine
          onShow={() => dispatch({ type: "show" })}
          onChange={(value) => dispatch({ type: "value", value })}
          onHide={() => dispatch({ type: "hide" })}
          onSubmit={handleSubmit}
          value={value}
          skeleton={loading}
          show={show}
        />
      )}
      <VoucherCodeDeal
        busy={busy}
        product={deal?.product}
        inCart={Boolean(deal?.cartItemLineItemId)}
        onClose={() => dispatch({ type: "hide" })}
        onContinue={() => {
          if (deal?.cartItemLineItemId) {
            dispatch({ type: "hide" });
            return;
          }

          if (deal?.product?.id) {
            dispatch({ type: "busy" });
            router.replace(
              `/${locationRef}/menu-item?productId=${encodeURIComponent(
                deal.product?.id,
              )}`,
            );
          }
        }}
      />

      {notLoggedIn && (
        <VoucherCodeNotLoggedIn
          code={value}
          onClick={() => dispatch({ type: "hide" })}
          onClickLogin={handleLogin}
        />
      )}

      {error && (
        <VoucherCodeDialog>
          <RedeemCodeError
            code={value}
            message={error}
            onRetryVoucherCode={() => {
              dispatch({ type: "hide" });
            }}
          />
        </VoucherCodeDialog>
      )}

      {voucher && (
        <ClaimSuccessModal
          onClose={handleContinue}
          collectionDescription={voucher.message}
          rewardType={voucher.rewardType}
        />
      )}
    </>
  );
}

const daveDeal: Deal = {
  product: {
    id: "DAVEORONI",
    title: "LOKE x Jet's Pizza 2024",
    available: true,
    price: { amount: 0, taxIncluded: 0 },
    tags: [],
    options: [],
    imageUrl: `/images/jets_easter_egg.jpg`,
  },
  cartItemLineItemId: undefined,
};
